import { useState, useRef, useCallback, useEffect } from 'react';

export const useConversationManager = (userId, dataStoreService) => {
  const [conversation, setConversation] = useState([]);
  const [currentChatGroupId, setCurrentChatGroupId] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [lastActivity, setLastActivity] = useState(null);

  // Debug conversation changes
  useEffect(() => {
    console.log('🔥 [ConversationManager] Conversation updated:', {
      length: conversation.length,
      sessionActive,
      userId,
      messages: conversation.map(msg => ({ type: msg.type, contentLength: msg.content?.length || 0 }))
    });
  }, [conversation.length, sessionActive, userId]);

  // =============================================================================
  // CORE SESSION MANAGEMENT
  // =============================================================================

  // Start session
  const startSession = useCallback(() => {
    console.log('🔥 [ConversationManager] Starting session');
    if (!sessionActive) {
      const now = new Date();
      setSessionActive(true);
      setSessionStartTime(now);
      setLastActivity(now);
      
      // Add session start message for tracking
      const sessionStartMessage = {
        id: `session_start_${Date.now()}`,
        type: 'system',
        content: 'Session started',
        timestamp: now.toISOString(),
        sessionEvent: true
      };
      
      setConversation(prev => [...prev, sessionStartMessage]);
    }
  }, [sessionActive]);

  // End session
  const endSession = useCallback(() => {
    console.log('🔥 [ConversationManager] Ending session');
    const now = new Date();
    setSessionActive(false);
    setLastActivity(now);
    
    // Add session end message for tracking
    const sessionEndMessage = {
      id: `session_end_${Date.now()}`,
      type: 'system',
      content: 'Session ended',
      timestamp: now.toISOString(),
      sessionEvent: true
    };
    
    setConversation(prev => {
      const updated = [...prev, sessionEndMessage];
      
      // Save to localStorage for chat group if available
      if (currentChatGroupId) {
        try {
          localStorage.setItem(`hume-conversation-${currentChatGroupId}`, JSON.stringify(updated));
          console.log('🔥 [ConversationManager] Conversation saved to localStorage for chat group:', currentChatGroupId);
        } catch (error) {
          console.error('Error saving to localStorage:', error);
        }
      }
      
      return updated;
    });
    
    // Don't reset sessionStartTime so journal can access it
  }, [currentChatGroupId]);

  // =============================================================================
  // MESSAGE MANAGEMENT
  // =============================================================================

  // Add message to conversation and save to storage
  const addMessageToConversation = useCallback((message) => {
    console.log('🔥 [ConversationManager] Adding message:', {
      type: message.type,
      contentLength: message.content?.length || 0,
      sessionActive
    });
    
    // Ensure message has required properties
    const processedMessage = {
      id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: message.type,
      content: message.content || '',
      timestamp: message.timestamp || new Date().toISOString(),
      emotions: message.emotions || [],
      ...message // Include any additional properties
    };
    
    // Auto-start session on first user message
    if (!sessionActive && message.type === 'user') {
      console.log('🔥 [ConversationManager] Auto-starting session on user message');
      const now = new Date();
      setSessionActive(true);
      setSessionStartTime(now);
      setLastActivity(now);
    }

    setConversation(prev => {
      const updated = [...prev, processedMessage];
      console.log('🔥 [ConversationManager] Conversation updated, total length:', updated.length);
      
      // Save to localStorage for chat group if available
      if (currentChatGroupId) {
        try {
          localStorage.setItem(`hume-conversation-${currentChatGroupId}`, JSON.stringify(updated));
          console.log('🔥 [ConversationManager] Conversation auto-saved to localStorage');
        } catch (error) {
          console.error('Error saving to localStorage:', error);
        }
      }
      
      return updated;
    });
    
    setLastActivity(new Date());
  }, [sessionActive, currentChatGroupId]);

  // Legacy alias for backward compatibility
  const addMessage = useCallback((message) => {
    addMessageToConversation(message);
  }, [addMessageToConversation]);

  // =============================================================================
  // DATA PERSISTENCE
  // =============================================================================

  // Save conversation to data store
  const saveConversationToDataStore = useCallback(async (summaryData = {}) => {
    if (!dataStoreService) {
      console.log('🔥 [ConversationManager] No data store service available');
      return;
    }
    
    try {
      const conversationStats = getConversationStats();
      
      const conversationData = {
        id: userId,
        conversation: conversation,
        summary: summaryData.summary || '',
        summaryHeader: summaryData.summaryHeader || '',
        insights: summaryData.insights || '',
        sessionStartTime: sessionStartTime,
        lastActivity: lastActivity,
        sessionActive: sessionActive,
        stats: conversationStats,
        savedAt: new Date().toISOString()
      };
      
      await dataStoreService.saveConversation(userId, conversationData);
      console.log('💾 [ConversationManager] Conversation saved to data store:', {
        userId,
        messageCount: conversation.length,
        stats: conversationStats
      });
    } catch (error) {
      console.error('[ConversationManager] Error saving conversation:', error);
    }
  }, [userId, conversation, sessionStartTime, lastActivity, sessionActive, dataStoreService]);

  // Load previous conversation
  const loadPreviousConversation = useCallback(async () => {
    if (!dataStoreService) {
      console.log('🔥 [ConversationManager] No data store service available for loading');
      return null;
    }
    
    try {
      const savedData = await dataStoreService.getConversation(userId);
      if (savedData) {
        console.log('📂 [ConversationManager] Loading previous conversation:', {
          userId,
          messageCount: savedData.conversation?.length || 0,
          hasStats: !!savedData.stats
        });
        
        setConversation(savedData.conversation || []);
        setSessionStartTime(savedData.sessionStartTime ? new Date(savedData.sessionStartTime) : null);
        setLastActivity(savedData.lastActivity ? new Date(savedData.lastActivity) : null);
        
        return {
          summary: savedData.summary || '',
          summaryHeader: savedData.summaryHeader || 'Today\'s Reflection',
          insights: savedData.insights || '',
          stats: savedData.stats || null
        };
      }
    } catch (error) {
      console.error('[ConversationManager] Error loading previous conversation:', error);
    }
    return null;
  }, [userId, dataStoreService]);

  // =============================================================================
  // CHAT GROUP MANAGEMENT
  // =============================================================================

  // Handle chat group ID changes
  const setChatGroupId = useCallback((chatGroupId) => {
    console.log('🔥 [ConversationManager] Setting chat group ID:', chatGroupId);
    setCurrentChatGroupId(chatGroupId);
  }, []);

  // Load conversation from chat group
  const loadConversationFromChatGroup = useCallback((chatGroupId) => {
    try {
      console.log('🔥 [ConversationManager] Loading conversation from chat group:', chatGroupId);
      const saved = localStorage.getItem(`hume-conversation-${chatGroupId}`);
      if (saved) {
        const savedConversation = JSON.parse(saved);
        setConversation(savedConversation);
        setCurrentChatGroupId(chatGroupId);
        console.log('📂 [ConversationManager] Loaded conversation from chat group:', {
          chatGroupId,
          messageCount: savedConversation.length
        });
        return savedConversation;
      }
    } catch (error) {
      console.error('[ConversationManager] Error loading conversation from chat group:', error);
    }
    return [];
  }, []);

  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================

  // Clear conversation
  const clearConversation = useCallback(() => {
    console.log('🔥 [ConversationManager] Clearing conversation');
    setConversation([]);
    setSessionActive(false);
    setSessionStartTime(null);
    setLastActivity(null);
    
    // Clear localStorage for current chat group
    if (currentChatGroupId) {
      try {
        localStorage.removeItem(`hume-conversation-${currentChatGroupId}`);
        console.log('🔥 [ConversationManager] Cleared localStorage for chat group:', currentChatGroupId);
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
    }
    
    setCurrentChatGroupId(null);
  }, [currentChatGroupId]);

  // Get conversation stats
  const getConversationStats = useCallback(() => {
    const userMessages = conversation.filter(msg => msg.type === 'user').length;
    const assistantMessages = conversation.filter(msg => msg.type === 'assistant').length;
    const systemMessages = conversation.filter(msg => msg.type === 'system').length;
    
    const meaningfulMessages = conversation.filter(msg => 
      msg.type !== 'system' && 
      msg.content && 
      msg.content.trim().length > 3 &&
      !msg.content.includes('Session started') &&
      !msg.content.includes('Session ended') &&
      !msg.sessionEvent
    ).length;
    
    const stats = {
      total: conversation.length,
      user: userMessages,
      assistant: assistantMessages,
      system: systemMessages,
      meaningful: meaningfulMessages,
      duration: sessionStartTime ? Date.now() - sessionStartTime.getTime() : 0,
      sessionStartTime: sessionStartTime?.toISOString() || null,
      lastActivity: lastActivity?.toISOString() || null
    };
    
    console.log('🔥 [ConversationManager] Conversation stats:', stats);
    return stats;
  }, [conversation, sessionStartTime, lastActivity]);

  // Check if conversation has meaningful content (for journal generation)
  const hasMeaningfulContent = useCallback(() => {
    const meaningfulMessages = conversation.filter(msg => 
      msg.type !== 'system' && 
      msg.content && 
      msg.content.trim().length > 3 &&
      !msg.content.includes('Session started') &&
      !msg.content.includes('Session ended') &&
      !msg.sessionEvent
    );
    
    const hasMeaningful = meaningfulMessages.length >= 1; // At least 1 meaningful message
    
    console.log('🔥 [ConversationManager] Checking meaningful content:', {
      totalMessages: conversation.length,
      meaningfulMessages: meaningfulMessages.length,
      hasMeaningful
    });
    
    return hasMeaningful;
  }, [conversation]);

  // Get meaningful messages only
  const getMeaningfulMessages = useCallback(() => {
    return conversation.filter(msg => 
      msg.type !== 'system' && 
      msg.content && 
      msg.content.trim().length > 3 &&
      !msg.content.includes('Session started') &&
      !msg.content.includes('Session ended') &&
      !msg.sessionEvent
    );
  }, [conversation]);

  // Force save conversation
  const forceSaveConversation = useCallback(() => {
    if (currentChatGroupId && conversation.length > 0) {
      try {
        localStorage.setItem(`hume-conversation-${currentChatGroupId}`, JSON.stringify(conversation));
        console.log('🔥 [ConversationManager] Force saved conversation to localStorage');
        
        // Also save to data store if available
        if (dataStoreService) {
          saveConversationToDataStore();
        }
        
        return true;
      } catch (error) {
        console.error('Error force saving conversation:', error);
        return false;
      }
    }
    return false;
  }, [currentChatGroupId, conversation, dataStoreService, saveConversationToDataStore]);

  // =============================================================================
  // RETURN HOOK API
  // =============================================================================

  return {
    // States
    conversation,
    currentChatGroupId,
    sessionActive,
    sessionStartTime,
    lastActivity,
    userId,

    // Core session management
    startSession,
    endSession,

    // Message management
    addMessageToConversation,
    addMessage, // Legacy alias

    // Data persistence
    saveConversationToDataStore,
    loadPreviousConversation,
    forceSaveConversation,

    // Chat group management
    setChatGroupId,
    loadConversationFromChatGroup,

    // Utility functions
    clearConversation,
    getConversationStats,
    hasMeaningfulContent,
    getMeaningfulMessages,

    // Direct setters (use sparingly, prefer methods above)
    setConversation,
    setSessionActive,
    setSessionStartTime,
    setLastActivity
  };
};